import { api } from '@/api/client';

export interface SettlementFilters {
  settlement_id?: string;
  settlement_status?: string;
  merchant_uuid?: string;
  page?: number;
  page_size?: number;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
}

export interface SettledTransactionItem {
  transaction_id: string;
  transaction_amount: string | number;
  service_charge: string | number;
  service_tax: string | number;
}

export interface SettlementItem {
  settlement_id: string;
  merchant_name: string;
  total_transaction_amount: string | number;
  settlement_amount: string | number;
  settlement_charges: string | number;
  settlement_date: string;
  is_settled: string | boolean;
  utr_number: string;
  payout_date_time: string;
  payout_amount: string | number;
  settled_transactions: SettledTransactionItem[];
}

export interface SettlementListResponse {
  success: boolean;
  data: SettlementItem[];
  count?: number;
}

export interface SettlementStatsResponse {
  success: boolean;
  data: {
    total_settled_amount: string | number;
    total_unsettled_amount: string | number;
    pending_settlements: string | number;
    total_settlements: string | number;
  };
}

export interface SettlementDownloadResponse {
  success: boolean;
  download_url?: string;
  message?: string;
}

// Generate report request interface
export interface GenerateReportRequest {
  report_name: string;
  request_report_type: 'quick' | 'scheduled';
  report_type: string;
  report_format: 'xlsx' | 'csv' | 'zip' | 'pdf';
  date_range_filter?: string;
  column_config: string[];
  report_start_date?: string;
  report_end_date?: string;
}

// Scheduled report request interface
export interface ScheduledReportRequest {
  report_name: string;
  request_report_type: 'scheduled';
  report_type: string;
  report_format: 'xlsx' | 'csv' | 'zip' | 'pdf';
  scheduled_frequency: 'daily' | 'weekly' | 'monthly';
  scheduled_time: string; // HH:MM:SS format
  scheduled_day?: number; // 0-6 for weekly (Monday=0)
  scheduled_date?: number; // 1-31 for monthly
  report_start_date: string;
  report_end_date: string;
  column_config: string[];
}

// Scheduled report response interface
export interface ScheduledReport {
  id: string;
  report_name: string;
  scheduled_frequency: 'daily' | 'weekly' | 'monthly';
  scheduled_time: string;
  scheduled_day?: number;
  scheduled_date?: number;
  report_format: string;
  column_config: string[];
  created_at?: string;
  is_active?: boolean;
}

// Scheduled report item from paginated API
export interface ScheduledReportItem {
  schedule_id: string;
  report_id: number;
  report_name: string;
  report_type: string;
  format: string;
  date_range: {
    start: string;
    end: string;
  };
  frequency: 'daily' | 'weekly' | 'monthly';
  day: number | null;
  date: number | null;
  time: string;
  next_run: string;
  created_at: string;
}

// Paginated scheduled reports response
export interface ScheduledReportsPaginatedResponse {
  success: boolean;
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  results: ScheduledReportItem[];
}

// Download report interface for the Downloads tab
export interface DownloadReport {
  id: string;
  file_name: string;
  creation_date: string;
  is_active: boolean;
  download_url?: string;
}

// Download reports API response interface
export interface DownloadReportsResponse {
  success: boolean;
  message: string;
  data: DownloadReport[];
}

const SETTLEMENT_ENDPOINTS = {
  LIST: '/merchant/v1/settlements/',
  FILTER: '/merchant/v1/settlements/',
  STATS: '/merchant/v1/settlements-stats/',
  DOWNLOAD: '/merchant/v1/settlements/download/',
  DETAIL: (id: string) => `/merchant/v1/settlements/${id}/`,
  GENERATE_REPORT: '/merchant/v1/generate-report/',
  SCHEDULED_REPORTS: '/merchant/v1/scheduled-reports/',
  MERCHANT_REPORTS: '/merchant/v1/merchant-reports/',
  DELETE_SCHEDULED_REPORT: (id: string, reportType: string) =>
    `/merchant/v1/delete-scheduled-report/${id}/?report_type=${reportType}`,
} as const;

class SettlementService {

  /**
   * Get paginated list of settlements
   */
  async getSettlements(page: number = 1, pageSize: number = 50): Promise<any> {
    try {
      const url = `${SETTLEMENT_ENDPOINTS.LIST}`;
      const response = await api.get<any>(url, {
        params: {
          page: page,
          page_size: pageSize,
        },
      });

      return response;

    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transactions');
    }
  }

  /**
   * Get transaction statistics
   */
  async getSettlementsStats(): Promise<any> {
    try {
      const response = await api.get<any>(SETTLEMENT_ENDPOINTS.STATS);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction statistics');
    }
  }


  /**
     * Get single transaction by ID
     */
  async getSettlementData(id?: string, merchantTransactionId?: string): Promise<any> {
    try {
      if (!id && !merchantTransactionId) {
        throw new Error('Either settlement_id or settlement_uuid must be provided');
      }

      const queryParams = new URLSearchParams();

      if (id) {
        queryParams.append('settlement_id', id);
      }

      if (merchantTransactionId) {
        queryParams.append('settlement_uuid', merchantTransactionId);
      }

      const url = `${SETTLEMENT_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction');
    }
  }


  /**
 * Get single settlements by ID
 */
  async getSettlementStatusData(status?: any): Promise<any> {
    try {
      if (!status) {
        throw new Error('Either status must be provided');
      }

      const queryParams = new URLSearchParams();

      if (status) {
        queryParams.append('settlement_status', status);
      }

      const url = `${SETTLEMENT_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction');
    }
  }


  /**
* Get date range
*/
  async getSettlementDateRangeData(startDate?: any, endDate?: any): Promise<any> {
    try {
      if (!startDate && !endDate) {
        throw new Error('Either Start Date and End Date must be provided');
      }

      const queryParams = new URLSearchParams();

      if (startDate) {
        queryParams.append('start_date', startDate);
      }

      if (endDate) {
        queryParams.append('end_date', endDate);
      }

      const url = `${SETTLEMENT_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction');
    }
  }

  async downloadSettlement(settlementId?: string): Promise<SettlementDownloadResponse> {
    const response = await api.post<SettlementDownloadResponse>('/merchant/v1/download-settlement/', {
      settlement_id: settlementId,
    });
    return response;
  }


  /**
     * Generate and download report
     */
  async generateReport(request: GenerateReportRequest): Promise<Blob> {
    try {
      const response = await api.post<any>(SETTLEMENT_ENDPOINTS.GENERATE_REPORT, request, {
        responseType: 'blob',
      });
      // Handle different response structures
      if (response instanceof Blob) {
        return response;
      }
      if (response && response.data instanceof Blob) {
        return response.data;
      }
      if (response && typeof response === 'object' && 'data' in response) {
        return new Blob([response.data]);
      }
      // Fallback: try to create blob from response
      return new Blob([response]);
    } catch (error) {
      throw this.handleError(error, 'Failed to generate report');
    }
  }

  /**
   * Create scheduled report
   */
  async createScheduledReport(request: ScheduledReportRequest): Promise<any> {
    try {
      const response = await api.post<any>(SETTLEMENT_ENDPOINTS.GENERATE_REPORT, request);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to create scheduled report');
    }
  }

  /**
   * Get scheduled reports list
   */
  async getScheduledReports(): Promise<ScheduledReport[]> {
    try {
      const response = await api.get<any>(SETTLEMENT_ENDPOINTS.SCHEDULED_REPORTS);
      return response?.data || response || [];
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch scheduled reports');
    }
  }

  /**
   * Get scheduled reports list with pagination
   */
  async getScheduledReportsPaginated(
    reportType: string = 'settlements',
    page: number = 1,
    pageSize: number = 20
  ): Promise<ScheduledReportsPaginatedResponse> {
    try {
      const url = `${SETTLEMENT_ENDPOINTS.SCHEDULED_REPORTS}?report_type=${reportType}&page=${page}&page_size=${pageSize}`;
      const response = await api.get<any>(url);
      return {
        success: response?.success ?? true,
        count: response?.count ?? 0,
        page: response?.page ?? page,
        page_size: response?.page_size ?? pageSize,
        total_pages: response?.total_pages ?? 1,
        results: response?.results || [],
      };
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch scheduled reports');
    }
  }

  /**
   * Delete scheduled report
   */
  async deleteScheduledReport(id: string, reportType: string = 'settlements'): Promise<void> {
    try {
      await api.delete(SETTLEMENT_ENDPOINTS.DELETE_SCHEDULED_REPORT(id, reportType));
    } catch (error) {
      throw this.handleError(error, 'Failed to delete scheduled report');
    }
  }

  /**
   * Get download reports list for the Downloads tab
   */
  async getDownloadReports(reportType: string = 'settlements'): Promise<any> {
    try {
      const response = await api.get<any>(`${SETTLEMENT_ENDPOINTS.MERCHANT_REPORTS}?report_type=${reportType}`);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch download reports');
    }
  }

  /**
 * Handle API errors
 */
  private handleError(error: unknown, defaultMessage: string): Error {
    if (error && typeof error === 'object' && 'message' in error) {
      return error as Error;
    }
    return new Error(defaultMessage);
  }
}

// Create singleton instance
export const settlementService = new SettlementService();

// Export for testing
export { SettlementService }


